import { useCallback, useState } from "react";
import { useShipmentByIdLazyQuery } from "@api/graphql/generated/generated-types";

/**
 * Resolves every date the moved orders touch before a transfer.
 *
 * The routing job moves whole orders, and it needs a vehicle for each date
 * their stops land on or it is rejected for a node/vehicle date mismatch. On a
 * multi-day order the sibling stops can sit on days the route list never
 * showed, so each shipment is looked up instead of trusting the checked stops'
 * dates alone.
 */
export function useTransferStopDates() {
  const [getShipmentById] = useShipmentByIdLazyQuery({
    fetchPolicy: "network-only",
  });
  const [isLoading, setIsLoading] = useState(false);

  const resolveTransferStopDates = useCallback(
    (
      courierId: string,
      shipmentIds: string[],
      knownDates: string[]
    ): Promise<string[]> => {
      const dates = new Set<string>(knownDates);
      setIsLoading(true);
      // One at a time: concurrent calls to the same lazy query can resolve
      // with each other's variables on this Apollo version.
      return shipmentIds
        .reduce<Promise<void>>(
          (chain, shipmentId) =>
            chain
              .then(() =>
                getShipmentById({ variables: { courierId, shipmentId } })
              )
              .then(({ data, error }) => {
                if (error) throw error;
                data?.shipmentById?.stops.forEach((stop) => {
                  if (stop.completedAt !== null) return;
                  const date = stop.effectiveStopDate || stop.stopDate;
                  if (date) dates.add(date);
                });
              }),
          Promise.resolve()
        )
        .then(() => Array.from(dates))
        .finally(() => setIsLoading(false));
    },
    [getShipmentById]
  );

  return { resolveTransferStopDates, isLoading };
}
